import React, { useState, useEffect, useMemo, useRef } from 'react';
import { reportHandledError } from '../utils/sentry';
import { supabase } from '../supabase';
import { useAuth } from '../context/AuthContext';
import {
  Target,
  Plus,
  Edit3,
  Trash2,
  ArrowLeft,
  RefreshCcw,
  Search,
  Info,
  X,
  Save,
  ChevronRight,
  Hash,
  Layers,
  List,
  LayoutGrid,
  Eye,
  EyeOff
} from 'lucide-react';
import toast from 'react-hot-toast';

/**
 * Gestión de Centros de Costo - Estándar Impulza Enterprise
 * Segmentación técnica de unidades de negocio para analítica financiera.
 *
 * UI Policy:
 * - SIN popouts/modales: editor inline embebido.
 * - Layout estándar Productos: Header+CTA / KPIs / Filtros / Listado.
 */
const CentrosCosto = ({ onNavigate }) => {
  const { profile } = useAuth();

  const [lista, setLista] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  // Layout estándar Productos
  const [layout, setLayout] = useState('lista'); // lista | grilla
  const [searchTerm, setSearchTerm] = useState('');

  // Editor inline (sin modales)
  const [editorOpen, setEditorOpen] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [nombre, setNombre] = useState('');
  const editorRef = useRef(null);

  // --- Mantengo tu estado showModal para NO "borrar" lógica previa ---
  // pero queda DEPRECADO por política: no usamos popouts.
  const [showModal, setShowModal] = useState(false); // DEPRECADO

  useEffect(() => {
    if (profile?.organization_id) fetchData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [profile?.organization_id]);

  const fetchData = async () => {
    try {
      setLoading(true);
      const { data, error } = await supabase
        .from('centros_costo')
        .select('*')
        .eq('organization_id', profile.organization_id)
        .order('nombre');

      if (error) throw error;
      setLista(data || []);
    } catch (error) {
      reportHandledError(error, { feature: 'tesoreria:centros-costo' });
      toast.error('Error al sincronizar estructura de costos');
    } finally {
      setLoading(false);
    }
  };

  const resetForm = () => {
    setEditingId(null);
    setNombre('');
  };

  const openNewInline = () => {
    resetForm();
    setEditorOpen(true);
    setTimeout(() => editorRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' }), 50);
  };

  const openEditInline = (item) => {
    setEditingId(item.id);
    setNombre(item.nombre || '');
    setEditorOpen(true);
    setTimeout(() => editorRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' }), 50);
  };

  const closeInline = () => {
    setEditorOpen(false);
    resetForm();
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!nombre.trim()) return toast.error('El nombre es obligatorio');

    setSaving(true);
    const toastId = toast.loading(editingId ? 'Actualizando unidad...' : 'Registrando centro operativo...');
    try {
      const payload = {
        nombre: nombre.toUpperCase(),
        organization_id: profile.organization_id,
      };

      if (editingId) {
        const { error } = await supabase.from('centros_costo').update(payload).eq('id', editingId);
        if (error) throw error;
        toast.success('Centro de costo actualizado', { id: toastId });
      } else {
        const { error } = await supabase.from('centros_costo').insert([payload]);
        if (error) throw error;
        toast.success('Nueva unidad de negocio registrada', { id: toastId });
      }

      closeInline();
      fetchData();
    } catch (error) {
      toast.error('Fallo en la persistencia técnica', { id: toastId });
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (id) => {
    const ok = window.confirm('¿CONFIRMAR ELIMINACIÓN DE ESTE CENTRO?\n\nPodría afectar reportes históricos.');
    if (!ok) return;

    const toastId = toast.loading('Eliminando...');
    try {
      const { error } = await supabase.from('centros_costo').delete().eq('id', id).eq('organization_id', profile.organization_id);
      if (error) throw error;

      toast.success('Centro removido', { id: toastId });
      fetchData();
    } catch (e) {
      toast.error('No se puede eliminar un centro con transacciones vinculadas', { id: toastId });
    }
  };

  const filteredLista = useMemo(() => {
    const needle = (searchTerm || '').toLowerCase().trim();
    if (!needle) return lista;
    return (lista || []).filter((item) => (item.nombre || '').toLowerCase().includes(needle));
  }, [lista, searchTerm]);

  // KPIs compactos estilo Productos
  const kpis = useMemo(() => {
    const total = (lista || []).length;
    const visibles = (filteredLista || []).length;

    const now = Date.now();
    const sevenDays = 7 * 24 * 60 * 60 * 1000;
    const nuevas7d = (lista || []).filter((c) => {
      const t = c?.created_at ? new Date(c.created_at).getTime() : null;
      return t && now - t <= sevenDays;
    }).length;

    return [
      { label: 'Centros', val: total, icon: <Target size={20} />, bg: 'bg-blue-50', text: 'text-blue-600' },
      { label: 'Visibles', val: visibles, icon: <Eye size={20} />, bg: 'bg-slate-50', text: 'text-slate-600' },
      { label: 'Nuevos 7d', val: nuevas7d, icon: <Layers size={20} />, bg: 'bg-emerald-50', text: 'text-emerald-600' },
      { label: 'Impacto', val: 'Gastos / Rentabilidad', icon: <Info size={20} />, bg: 'bg-amber-50', text: 'text-amber-600', isInfo: true },
    ];
  }, [lista, filteredLista]);

  if (loading && lista.length === 0) {
    return (
      <div className="h-screen flex flex-col items-center justify-center bg-[#F8FAFC]">
        <RefreshCcw className="animate-spin text-[#51A7FD] mb-4" size={40} />
        <p className="font-bold uppercase tracking-[0.3em] text-slate-400 text-[10px]">Mapeando Estructura de Costos...</p>
      </div>
    );
  }

  return (
    <div className="w-full p-3 md:p-4 bg-[#F8FAFC] min-h-screen font-sans text-slate-700">
      {/* HEADER: Modelo Productos (título + acción) */}
      <div className="flex flex-col lg:flex-row lg:items-center justify-between gap-4 mb-4 px-1">
        <div className="flex items-start gap-3">
          <div className="p-2 bg-white border border-slate-200 rounded-lg text-slate-400 shadow-sm mt-0.5">
            <Target size={18} />
          </div>

          <div>
            <button
              onClick={() => onNavigate?.('configuracion')}
              className="text-slate-400 text-[9px] font-black uppercase mb-1 flex items-center gap-2 hover:text-[#51A7FD] transition-colors tracking-widest"
            >
              <ArrowLeft size={14} /> Volver a Configuración
            </button>

            <h1 className="text-lg md:text-xl font-bold text-slate-800 uppercase tracking-tight">Centros de Costo</h1>
            <p className="text-[9px] font-medium text-slate-400 uppercase tracking-widest mt-0.5 italic">
              Segmentación de egresos y rentabilidad por unidad operativa
            </p>
          </div>
        </div>

        <div className="flex flex-wrap gap-2 justify-end">
          <button
            onClick={async () => {
              await fetchData();
              toast.success('Estructura sincronizada');
            }}
            className="bg-white border border-slate-200 text-slate-600 px-4 py-2.5 rounded-xl shadow-sm hover:bg-slate-50 transition-all flex items-center gap-2 text-[10px] font-bold uppercase tracking-widest"
          >
            <RefreshCcw size={16} /> Sincronizar
          </button>

          <button
            onClick={openNewInline}
            className="bg-[#51A7FD] text-white px-6 py-2.5 rounded-xl shadow-md shadow-blue-100 hover:scale-[1.02] active:scale-95 transition-all flex items-center gap-2 text-[10px] font-bold uppercase tracking-widest justify-center"
          >
            <Plus size={16} /> Nuevo Centro
          </button>
        </div>
      </div>

      {/* KPIs compactos */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-3 mb-4">
        {kpis.map((k, idx) => (
          <div
            key={idx}
            className="bg-white p-4 rounded-2xl border border-slate-200 shadow-sm flex items-center gap-4 hover:border-blue-200 transition-all"
          >
            <div className={`p-3 rounded-xl ${k.bg} ${k.text} shadow-inner`}>{k.icon}</div>
            <div className="min-w-0">
              <p className="text-[9px] font-black text-slate-400 uppercase tracking-widest leading-none mb-1">{k.label}</p>
              {k.isInfo ? (
                <p className="text-[9px] font-bold text-slate-500 uppercase leading-tight">{k.val}</p>
              ) : (
                <p className="text-xl font-black text-slate-800 tracking-tighter leading-none">{k.val}</p>
              )}
            </div>
          </div>
        ))}
      </div>

      {/* Filtros / Vista (misma altura que Productos) */}
      <div className="bg-white p-3 rounded-xl shadow-sm border border-slate-200 mb-4 grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3">
        <div className="space-y-1">
          <label className="text-[8px] font-bold text-slate-400 uppercase tracking-widest ml-1">Búsqueda</label>
          <div className="relative">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-300" size={14} />
            <input
              type="text"
              placeholder="Buscar centro por nombre..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="w-full bg-slate-50 border border-slate-100 pl-9 pr-3 py-1.5 rounded-lg text-[10px] font-bold uppercase outline-none focus:border-[#51A7FD] transition-all shadow-inner"
            />
          </div>
        </div>

        <div className="space-y-1">
          <label className="text-[8px] font-bold text-slate-400 uppercase tracking-widest ml-1">Vista</label>
          <div className="flex bg-slate-50 border border-slate-100 rounded-lg p-0.5 shadow-inner h-[32px]">
            <button
              onClick={() => setLayout('lista')}
              className={`flex-1 rounded-md text-[8px] font-bold uppercase transition-all ${
                layout === 'lista' ? 'bg-white text-[#51A7FD] shadow-sm' : 'text-slate-400'
              }`}
              title="Lista"
            >
              <List size={10} className="inline mr-1" /> Lista
            </button>
            <button
              onClick={() => setLayout('grilla')}
              className={`flex-1 rounded-md text-[8px] font-bold uppercase transition-all ${
                layout === 'grilla' ? 'bg-white text-[#51A7FD] shadow-sm' : 'text-slate-400'
              }`}
              title="Grilla"
            >
              <LayoutGrid size={10} className="inline mr-1" /> Grilla
            </button>
          </div>
        </div>

        <div className="hidden lg:flex items-center gap-3 px-3 py-2 bg-blue-50/30 rounded-lg border border-blue-100">
          <Info size={16} className="text-[#51A7FD] shrink-0" />
          <p className="text-[8px] font-bold text-blue-600 uppercase leading-relaxed italic">
            Recomendación: sumar is_active + archived_at para baja lógica (evitar delete en históricos).
          </p>
        </div>
      </div>

      {/* EDITOR INLINE (sin popout) */}
      <div ref={editorRef} className="mb-4">
        {editorOpen && (
          <div className="bg-white rounded-2xl border border-slate-200 shadow-sm overflow-hidden">
            <div className="flex items-center justify-between gap-3 px-4 py-3 bg-slate-50/60 border-b border-slate-100">
              <div className="flex items-center gap-2">
                <div className="p-2 rounded-lg bg-white border border-slate-200 text-slate-400">
                  <Target size={18} />
                </div>
                <div>
                  <p className="text-[9px] font-black text-slate-400 uppercase tracking-widest">
                    {editingId ? 'Editar Centro de Costo' : 'Nuevo Centro de Costo'}
                  </p>
                  <p className="text-[8px] font-bold text-slate-500 uppercase tracking-widest">
                    Parámetro para imputación y analítica
                  </p>
                </div>
              </div>

              <button
                onClick={closeInline}
                className="bg-white border border-slate-200 text-slate-600 px-3 py-2 rounded-xl shadow-sm hover:bg-slate-50 transition-all flex items-center gap-2 text-[9px] font-bold uppercase tracking-widest"
              >
                <X size={14} /> Cerrar
              </button>
            </div>

            <form onSubmit={handleSubmit} className="p-4 grid grid-cols-1 md:grid-cols-3 gap-3">
              <div className="space-y-1 md:col-span-3">
                <label className="text-[8px] font-bold text-slate-400 uppercase tracking-widest ml-1">
                  Nombre Identificador
                </label>
                <div className="relative">
                  <Hash className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-300" size={14} />
                  <input
                    required
                    autoFocus
                    type="text"
                    value={nombre}
                    onChange={(e) => setNombre(e.target.value)}
                    className="w-full bg-slate-50 border border-slate-100 pl-9 pr-3 py-2 rounded-lg text-[10px] font-bold uppercase outline-none focus:border-[#51A7FD] transition-all shadow-inner"
                    placeholder="EJ: SUCURSAL NORTE, ÁREA TALLER..."
                  />
                </div>
              </div>

              <div className="md:col-span-3 flex flex-wrap gap-2 justify-end pt-1">
                <button
                  type="button"
                  onClick={closeInline}
                  className="bg-white border border-slate-200 text-slate-600 px-4 py-2 rounded-xl shadow-sm hover:bg-slate-50 transition-all flex items-center gap-2 text-[10px] font-bold uppercase tracking-widest"
                >
                  <X size={14} /> Cancelar
                </button>

                <button
                  type="submit"
                  disabled={saving}
                  className="bg-[#3FF2AA] text-[#0B2A36] px-6 py-2 rounded-xl font-black text-[10px] uppercase tracking-widest shadow-md shadow-emerald-500/10 hover:scale-[1.02] active:scale-95 transition-all flex items-center gap-2 disabled:opacity-50"
                >
                  {saving ? <RefreshCcw className="animate-spin" size={16} /> : <Save size={16} />}
                  {editingId ? 'Guardar Cambios' : 'Registrar Centro'}
                </button>
              </div>
            </form>
          </div>
        )}
      </div>

      {/* LISTADO (default: Lista) */}
      {layout === 'lista' && (
        <div className="bg-white rounded-xl shadow-sm border border-slate-200 overflow-hidden">
          <div className="overflow-x-auto">
            <table className="w-full text-left">
              <thead>
                <tr className="bg-slate-50/50 border-b border-slate-100 text-[8px] font-bold text-slate-400 uppercase tracking-widest">
                  <th className="px-4 py-3">#</th>
                  <th className="px-4 py-3">Centro</th>
                  <th className="px-4 py-3 text-center">Estado</th>
                  <th className="px-4 py-3 text-right">Acciones</th>
                  <th className="px-4 py-3 w-10"></th>
                </tr>
              </thead>

              <tbody className="divide-y divide-slate-50">
                {filteredLista.map((item, idx) => (
                  <tr
                    key={item.id}
                    className="hover:bg-blue-50/30 transition-all group"
                    onDoubleClick={() => openEditInline(item)}
                    title="Doble click para editar"
                  >
                    <td className="px-4 py-2">
                      <span className="text-[10px] font-bold text-slate-300 uppercase">#{idx + 1}</span>
                    </td>

                    <td className="px-4 py-2">
                      <div className="flex items-center gap-3">
                        <div className="w-8 h-8 rounded-lg bg-slate-50 flex items-center justify-center text-slate-400 border border-slate-100 shrink-0">
                          <Target size={14} />
                        </div>
                        <div className="flex flex-col">
                          <span className="text-[11px] font-bold text-slate-700 uppercase tracking-tight group-hover:text-[#51A7FD] transition-colors">
                            {item.nombre}
                          </span>
                          <span className="text-[8px] font-bold text-slate-400 uppercase">
                            Segmentación de gastos / rentabilidad
                          </span>
                        </div>
                      </div>
                    </td>

                    <td className="px-4 py-2 text-center">
                      <span className="text-[8px] font-bold px-2 py-0.5 rounded-md uppercase border bg-blue-50 text-[#51A7FD] border-blue-100">
                        Activo
                      </span>
                    </td>

                    <td className="px-4 py-2 text-right">
                      <div className="flex justify-end gap-1">
                        <button
                          onClick={(e) => {
                            e.stopPropagation();
                            openEditInline(item);
                          }}
                          className="p-2 text-slate-400 hover:text-[#51A7FD] hover:bg-slate-100 rounded-lg transition-all"
                          title="Editar"
                        >
                          <Edit3 size={16} />
                        </button>
                        <button
                          onClick={(e) => {
                            e.stopPropagation();
                            handleDelete(item.id);
                          }}
                          className="p-2 text-slate-400 hover:text-rose-500 hover:bg-rose-50 rounded-lg transition-all"
                          title="Eliminar"
                        >
                          <Trash2 size={16} />
                        </button>
                      </div>
                    </td>

                    <td className="px-4 py-2 text-right">
                      <ChevronRight size={14} className="text-slate-200 group-hover:text-[#51A7FD] group-hover:translate-x-1 transition-all" />
                    </td>
                  </tr>
                ))}

                {filteredLista.length === 0 && (
                  <tr>
                    <td colSpan="5" className="py-16 text-center opacity-30 italic text-[10px] font-black uppercase tracking-widest">
                      Sin centros operativos registrados
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {/* GRILLA (tu diseño original, conservado y normalizado en densidad) */}
      {layout === 'grilla' && (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
          {filteredLista.map((item) => (
            <div
              key={item.id}
              className="p-6 bg-white rounded-2xl border border-slate-100 flex items-center justify-between group hover:shadow-xl hover:border-blue-100 transition-all relative overflow-hidden"
            >
              <div className="flex items-center gap-4">
                <div className="w-12 h-12 rounded-xl bg-slate-50 text-slate-300 flex items-center justify-center group-hover:bg-blue-50 group-hover:text-[#51A7FD] transition-colors shadow-inner">
                  <Target size={22} />
                </div>
                <div>
                  <span className="font-black text-slate-700 text-[12px] uppercase tracking-tight leading-none">
                    {item.nombre}
                  </span>
                  <p className="text-[8px] font-bold text-slate-300 uppercase tracking-widest mt-1.5">Centro Activo</p>
                </div>
              </div>
              <div className="flex items-center gap-1">
                <button
                  onClick={() => openEditInline(item)}
                  className="p-2.5 text-slate-300 hover:text-[#51A7FD] hover:bg-blue-50 rounded-xl transition-all"
                  title="Configurar"
                >
                  <Edit3 size={18} />
                </button>
                <button
                  onClick={() => handleDelete(item.id)}
                  className="p-2.5 text-slate-100 hover:text-rose-500 hover:bg-rose-50 rounded-xl transition-all"
                  title="Remover"
                >
                  <Trash2 size={18} />
                </button>
              </div>
            </div>
          ))}

          {filteredLista.length === 0 && (
            <div className="col-span-full py-20 text-center opacity-20 flex flex-col items-center">
              <Layers size={48} strokeWidth={1} className="mb-4" />
              <p className="text-[10px] font-black uppercase tracking-[0.3em]">Sin centros operativos registrados</p>
            </div>
          )}
        </div>
      )}

      {/* MODAL ORIGINAL (DEPRECADO) - no se usa */}

      <style>{`
        .custom-scrollbar::-webkit-scrollbar { width: 4px; }
        .custom-scrollbar::-webkit-scrollbar-thumb { background: #cbd5e1; border-radius: 20px; }
      `}</style>
    </div>
  );
};

export default CentrosCosto;
